// Parent-side of the evisio2 ↔ inner-webtool postMessage bridge.
// Owned by the evisio2Companion widget. Mirrors the EvisioBridgeClient design with the
// addition of a command queue that survives iframe reloads (commands queued while the
// child is loading get flushed on the next `ready`).
//
// TODO(evisio-sdk): wire SDK tool calls to bridgeHost.sendCommandAwaitingResult(...) once
// the evisio2 SDK is ready. e.g. for openEntityView:
//   bridgeHost.sendCommandAwaitingResult({
//     kind: "evisio2:command",
//     type: "openEntityView",
//     payload: { entityId, entityClass },
//   }).then(...).catch((err: EvisioBridgeErrorShape) => ... surface to LLM ...);

import topic from 'dojo/topic';
import lang from 'dojo/_base/lang';
import {
    BRIDGE_NAMESPACE_EVENT,
    BRIDGE_TOPIC,
    COMMAND_RESULT_TIMEOUT_SECONDS,
    COMMAND_STALE_MS,
    CommandResultPayload,
    EvisioBridgeCommand,
    EvisioBridgeCommandName,
    EvisioBridgeErrorShape,
    QueuedCommand,
    ReadyPayload,
    Theme,
    ThemeChangedPayload,
    UrlChangedPayload,
} from "./EvisioBridgeTypes";

const SINGLETON_KEY = "__evisio2BridgeHost";

class EvisioBridgeHost {
    public isActive: boolean = true;

    private iframe: HTMLIFrameElement | null = null;
    private _normalizedOrigin: string;

    private _bridgeReady: boolean = false;
    private _lastReadyTs: number = 0;
    private _lastEmittedTheme: Theme | null = null;
    private _queue: QueuedCommand[] = [];

    private _themeSubHandle: any = null;

    private _pendingRequests: {
        [requestId: string]: {
            resolve: (v: any) => void;
            reject: (e: EvisioBridgeErrorShape) => void;
            timeoutHandle: number;
        }
    } = {};
    private _requestIdPrefix: string;
    private _requestIdCounter: number = 0;

    // Snapshot of the child's available-command list from the last ready payload.
    // Empty until ready fires; sendCommandAwaitingResult falls through to the queue
    // in that case so pre-ready commands still buffer normally.
    private _availableCommands: EvisioBridgeCommandName[] = [];

    public static instance(iframe: HTMLIFrameElement): EvisioBridgeHost {
        let existing = (window as any)[SINGLETON_KEY];
        if (existing && existing instanceof EvisioBridgeHost && existing.isActive){
            existing.iframe = iframe; // rebind in case the caller passed a different ref
            return existing;
        }
        let inst = new EvisioBridgeHost(iframe);
        (window as any)[SINGLETON_KEY] = inst;
        return inst;
    }

    private constructor(iframe: HTMLIFrameElement){
        this.iframe = iframe;

        try {
            this._normalizedOrigin = new URL(window.location.href).origin;
        } catch (e){
            this._normalizedOrigin = (window.location as any).origin || "";
        }

        // performance.now() picked once at construct + monotonic counter → unique requestIds
        // without Math.random(). Doesn't need to be globally unique, only unique within this host.
        this._requestIdPrefix = "rq" + Math.floor(performance.now()).toString(36) + "-";

        window.addEventListener("message", this._onMessage);

        this._themeSubHandle = topic.subscribe("Theme/Change", lang.hitch(this, this._onLocalThemeChange));

        //RMTHIS-START — dev-only test hook on window.evisioTestTool. Delete this whole block
        //RMTHIS — once the SDK is wired and fires commands directly. grep "RMTHIS" before merging.
        let testInst = this;
        let wrap = function(label: string, p: Promise<any>): Promise<any> {
            p.then(
                function(r){ console.log("[evisioTestTool] " + label + " OK", r); },
                function(e){ console.warn("[evisioTestTool] " + label + " ERR", e); }
            );
            return p;
        };
        (window as any).evisioTestTool = {
            openEntityView: function(entityId: string, entityClass?: string){
                return wrap("openEntityView", testInst.sendCommandAwaitingResult({
                    kind: "evisio2:command",
                    type: "openEntityView",
                    payload: { entityId: entityId, entityClass: entityClass },
                }));
            },
            highlightCalendarOverview: function(entityId: string){
                return wrap("highlightCalendarOverview", testInst.sendCommandAwaitingResult({
                    kind: "evisio2:command",
                    type: "highlightCalendarOverview",
                    payload: { entityId: entityId },
                }));
            },
            openEmailEditor: function(opts: any){
                return wrap("openEmailEditor", testInst.sendCommandAwaitingResult({
                    kind: "evisio2:command",
                    type: "openEmailEditor",
                    payload: opts || {},
                }));
            },
            listAvailableCommands: function(){
                return testInst.getAvailableCommands();
            },
            listSdkTools: function(){
                // Reads the wiring singleton bound by Evisio2SdkWiring.connect() — empty
                // list before SDK connects, full list after the bridge↔SDK link is up.
                let w = (window as any).__evisio2SdkWiring;
                if (!w || typeof w.getRegisteredToolNames !== "function") return [];
                return w.getRegisteredToolNames();
            },
        };
        console.log("[evisioTestTool] installed — try evisioTestTool.listAvailableCommands(), evisioTestTool.listSdkTools(), evisioTestTool.openEntityView(\"<id>\"), evisioTestTool.highlightCalendarOverview(\"<bookingId>\") or evisioTestTool.openEmailEditor({ subject, plainTextBody, to: [\"<mail>\"] })");
        //RMTHIS-END
    }

    private _mintRequestId(): string {
        this._requestIdCounter++;
        return this._requestIdPrefix + this._requestIdCounter.toString(36);
    }

    // The SDK queries this before tool registration so it only exposes commands the user
    // can actually invoke. Returns a defensive copy. Empty until ready has fired.
    public getAvailableCommands(): EvisioBridgeCommandName[] {
        return this._availableCommands.slice();
    }

    public isCommandAvailable(name: EvisioBridgeCommandName): boolean {
        return this._availableCommands.indexOf(name) !== -1;
    }

    // Send a command and await its result. The child must answer with a `commandResult`
    // event carrying the same requestId. Resolves with CommandResultPayload.result on
    // success; rejects with EvisioBridgeErrorShape on handler-side failure or after the
    // hardcoded final-fallback timeout (COMMAND_RESULT_TIMEOUT_SECONDS).
    public sendCommandAwaitingResult<T = any>(cmd: EvisioBridgeCommand, survivesReload: boolean = true): Promise<T> {
        if (!this.isActive){
            return Promise.reject<T>({ kind: "handlerThrew", message: "bridge inactive" } as EvisioBridgeErrorShape);
        }

        // Defense-in-depth: if ready has fired and the command isn't available for this user,
        // reject immediately instead of postMessage-round-tripping. setTheme exempted (no
        // requestId protocol). Empty list = pre-ready, fall through to existing queue logic.
        if (cmd.type !== "setTheme" && this._availableCommands.length && !this.isCommandAvailable(cmd.type as EvisioBridgeCommandName)){
            return Promise.reject<T>({ kind: "roleDenied", message: "command " + cmd.type + " not available for this user" } as EvisioBridgeErrorShape);
        }

        let requestId = this._mintRequestId();
        let cmdWithMeta: EvisioBridgeCommand = Object.assign({}, cmd, { meta: Object.assign({}, cmd.meta || {}, { requestId: requestId }) }) as EvisioBridgeCommand;

        return new Promise<T>((resolve, reject) => {
            let timeoutHandle = window.setTimeout(() => {
                if (!this._pendingRequests[requestId]) return;
                delete this._pendingRequests[requestId];
                reject({ kind: "handlerThrew", message: "commandResult timeout" } as EvisioBridgeErrorShape);
            }, COMMAND_RESULT_TIMEOUT_SECONDS * 1000);

            this._pendingRequests[requestId] = { resolve: resolve, reject: reject, timeoutHandle: timeoutHandle };
            this.sendCommand(cmdWithMeta, survivesReload);
        });
    }

    // Called by evisio2Companion from iframe.onload BEFORE syncParentUrlFromIframe.
    // Flips bridgeReady false until the new document's client sends `ready`.
    public markIframeLoading(): void {
        if (!this.isActive) return;
        this._bridgeReady = false;
    }

    public sendCommand(cmd: EvisioBridgeCommand, survivesReload: boolean = true): void {
        if (!this.isActive) return;

        if (!this._bridgeReady){
            this._queue.push({ cmd: cmd, enqueuedAt: Date.now(), survivesReload: survivesReload });
            return;
        }

        if (!this._postToChild(cmd)){
            // Send failed (closed/navigating window). Re-queue for next ready.
            this._queue.push({ cmd: cmd, enqueuedAt: Date.now(), survivesReload: survivesReload });
            this._bridgeReady = false;
        }
    }

    private _postToChild(cmd: EvisioBridgeCommand): boolean {
        let win = this.iframe ? this.iframe.contentWindow : null;
        if (!win) return false;
        try {
            win.postMessage(cmd, this._normalizedOrigin);
            return true;
        } catch (e){
            console.warn("EvisioBridgeHost: postMessage to child failed", e);
            return false;
        }
    }

    private _flushQueue(): void {
        let now = Date.now();
        let remaining: QueuedCommand[] = [];

        for (let i = 0; i < this._queue.length; i++){
            let entry = this._queue[i];

            // Drop stale entries.
            if (now - entry.enqueuedAt > COMMAND_STALE_MS) continue;

            // Drop entries that don't survive reloads if a reload happened after enqueue.
            if (!entry.survivesReload && this._lastReadyTs > entry.enqueuedAt) continue;

            if (!this._postToChild(entry.cmd)){
                // Stop on first failure; keep this and following entries for next flush.
                remaining = remaining.concat(this._queue.slice(i));
                this._bridgeReady = false;
                break;
            }
        }

        this._queue = remaining;
    }

    private _onMessage = (ev: MessageEvent): void => {
        if (!this.isActive) return;

        // Source check — iframe.contentWindow is read live so it always points at the
        // current document's window after reloads.
        let expectedSource = this.iframe ? this.iframe.contentWindow : null;
        if (!expectedSource || ev.source !== expectedSource) return;

        if (ev.origin !== this._normalizedOrigin) return;

        let data: any = ev.data;
        if (typeof data === "string"){
            try { data = JSON.parse(data); } catch { return; }
        }
        if (!data || typeof data !== "object") return;

        // Silent filter — Teams/Syncfusion postMessages must not log.
        if (data.kind !== BRIDGE_NAMESPACE_EVENT) return;

        switch (data.type){
            case "ready":
                this._handleReady(data.payload as ReadyPayload);
                break;
            case "themeChanged":
                this._handleThemeChanged(data.payload as ThemeChangedPayload);
                break;
            case "urlChanged":
                this._handleUrlChanged(data.payload as UrlChangedPayload);
                break;
            case "commandResult":
                this._handleCommandResult(data.payload as CommandResultPayload);
                break;
            case "emailEditorOpened":
                topic.publish(BRIDGE_TOPIC.EmailEditorOpened, data.payload || {});
                break;
            case "emailEditorClosed":
                topic.publish(BRIDGE_TOPIC.EmailEditorClosed, data.payload || {});
                break;
            default:
                console.warn("EvisioBridgeHost: unknown event type", data.type);
        }
    };

    private _handleCommandResult(payload: CommandResultPayload): void {
        if (!payload || !payload.requestId) return;
        let pending = this._pendingRequests[payload.requestId];
        if (!pending) return; // unknown or already-timed-out request — silently drop
        clearTimeout(pending.timeoutHandle);
        delete this._pendingRequests[payload.requestId];
        if (payload.ok){
            pending.resolve(payload.result);
        } else {
            pending.reject(payload.error || { kind: "handlerThrew", message: "no error provided" });
        }
    }

    private _handleReady(payload: ReadyPayload): void {
        if (!payload) return;

        this._bridgeReady = true;
        this._lastReadyTs = Date.now();

        // Seed loop guard from the child's reported theme so we don't immediately
        // echo it back. localStorage is shared same-origin so both sides agree at boot.
        if (payload.theme === "light" || payload.theme === "dark" || payload.theme === "auto"){
            this._lastEmittedTheme = payload.theme;
        }

        this._availableCommands = Array.isArray(payload.availableCommands) ? payload.availableCommands.slice() : [];

        topic.publish(BRIDGE_TOPIC.Ready, payload);

        this._flushQueue();
    }

    private _handleThemeChanged(payload: ThemeChangedPayload): void {
        if (!payload || !payload.theme) return;
        if (payload.theme !== "light" && payload.theme !== "dark" && payload.theme !== "auto") return;

        let toggler: any = (window as any).themeToggler;
        if (!toggler || typeof toggler.setTheme !== "function") return;

        let current = this._readStoredTheme();
        if (current === payload.theme) return; // loop break

        this._lastEmittedTheme = payload.theme;
        toggler.setTheme(payload.theme);

        topic.publish(BRIDGE_TOPIC.ThemeChanged, payload);
    }

    private _handleUrlChanged(payload: UrlChangedPayload): void {
        if (!payload) return;
        topic.publish(BRIDGE_TOPIC.UrlChanged, payload);
    }

    private _readStoredTheme(): Theme | null {
        let toggler: any = (window as any).themeToggler;
        if (!toggler || typeof toggler.getTheme !== "function") return null;
        try {
            let stored = toggler.getTheme(false);
            if (stored === "light" || stored === "dark" || stored === "auto") return stored;
        } catch (e){ /* ignore */ }
        return null;
    }

    private _onLocalThemeChange(_theme: any): void {
        if (!this.isActive) return;

        let current = this._readStoredTheme();
        if (!current) return;
        if (current === this._lastEmittedTheme) return; // loop break

        this._lastEmittedTheme = current;
        this.sendCommand({ kind: "evisio2:command", type: "setTheme", payload: { theme: current } });
    }

    public destroy(): void {
        if (!this.isActive) return;
        this.isActive = false;

        window.removeEventListener("message", this._onMessage);

        if (this._themeSubHandle){
            try { this._themeSubHandle.remove(); } catch { /* ignore */ }
            this._themeSubHandle = null;
        }

        // Reject all pending requests so SDK-side awaiters don't hang past bridge teardown.
        for (let requestId in this._pendingRequests){
            let pending = this._pendingRequests[requestId];
            clearTimeout(pending.timeoutHandle);
            try { pending.reject({ kind: "handlerThrew", message: "bridge destroyed" }); } catch { /* ignore */ }
        }
        this._pendingRequests = {};

        this._queue = [];
        this.iframe = null;

        try { delete (window as any)[SINGLETON_KEY]; } catch { (window as any)[SINGLETON_KEY] = undefined; }
    }
}

export default EvisioBridgeHost;
